/**
 * features/customers/CustomersPage.tsx
 */

import { useState } from 'react';
import { Pencil, Plus, Search, Trash2, Users } from 'lucide-react';
import { formatTaxId } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { ApiError } from '~/lib/api';
import type { Party } from '~/lib/types';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, useToast } from '~/ui/overlays';
import { useCustomers, useDeleteCustomer } from './hooks';
import { CustomerFormDialog } from './CustomerFormDialog';

const PAGE_SIZE = 15;

export function CustomersPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [deleting, setDeleting] = useState<Party | null>(null);

  const { data, isLoading, isFetching } = useCustomers({ page, pageSize: PAGE_SIZE, search });
  const deleteCustomer = useDeleteCustomer();
  const toast = useToast();

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteCustomer.mutateAsync(deleting.id);
      toast.success('ลบลูกค้าแล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <PageHeader
        title="ลูกค้า"
        description="ข้อมูลลูกค้าที่ใช้ในใบเสนอราคาและใบกำกับภาษี"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus size={16} />
            เพิ่มลูกค้า
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <div className="relative w-full max-w-xs">
            <Search
              size={15}
              className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle"
              aria-hidden
            />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาชื่อ, เลขภาษี, เบอร์โทร"
              className="pl-8"
            />
          </div>
          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={4} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<Users size={28} />}
            title={search ? 'ไม่พบลูกค้าที่ค้นหา' : 'ยังไม่มีข้อมูลลูกค้า'}
            description={search ? undefined : 'เพิ่มลูกค้าเพื่อใช้ในการออกใบเสนอราคา'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>ชื่อลูกค้า</th>
                    <th>เลขผู้เสียภาษี</th>
                    <th>โทรศัพท์</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((customer) => (
                    <tr key={customer.id}>
                      <td>
                        <p className="font-medium text-fg">{customer.name}</p>
                        {customer.email ? (
                          <p className="text-xs text-fg-muted">{customer.email}</p>
                        ) : null}
                      </td>
                      <td className="tabular text-fg-muted">
                        {customer.taxId ? formatTaxId(customer.taxId) : '-'}
                      </td>
                      <td className="text-fg-muted">{customer.phone || '-'}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="แก้ไข"
                            onClick={() => {
                              setEditing(customer);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="ลบ"
                            onClick={() => setDeleting(customer)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <CustomerFormDialog open={formOpen} onClose={() => setFormOpen(false)} customer={editing} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบลูกค้า"
        message={`ต้องการลบ "${deleting?.name}" ใช่หรือไม่ การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteCustomer.isPending}
      />
    </div>
  );
}
